<script lang="ts" setup>
import { showConfirmDialog, showFailToast, showLoadingToast } from "vant";
import { onMounted, ref } from "vue";
import { usePrompts } from "@/stores/prompts";
import { useMateTrait } from "@/stores/mateTrait";
import { useI18n } from "vue-i18n";
import { useTask } from "@/stores/task";
import {
  initStyleWithInitData,
  decodePosesFromEnv,
  checkMatesHasGirlOrBoy,
} from "./Tabs/initStyle";
import { getInitPromptJson, trim } from "@/modules/common";
import { useGlobalUser } from "@/stores/globalUser";
import {
  sessionStorageGet,
  sessionStorageRemove,
} from "@/tools/sessionStorage";
import NSFWModalConfirmAge from "@/views/NSFW/Modal/NSFWModalConfirmAge.vue";
import { StorageGet, StorageSet } from "@/tools/localStorage";
import { useRoute } from "vue-router";
import { useTextImage } from "@/stores/textImage";
import { useGlobalModal } from "@/stores/globalModal";

// State
const route = useRoute();
const loadingPrompts = ref(false);
const traitStore = useMateTrait();
const userStore = useGlobalUser();

const showTabs = ref(["people", "layout", "env", "style", "setting"]);
const promptStore = usePrompts();

const { t } = useI18n();
// 画面风格
// 新建特征引导
const showCreateTraitGuide = ref(false);

// Lifecycle
const trait = useMateTrait();
const taskNewStore = useTask();
const textImageStore = useTextImage();
const reqData = ref<PromptJson>(getInitPromptJson());
/** 根据taskId回填数据 */
const writeBackByTextImage = (textImage: TextImage, mode?: number) => {
  const entity = textImage;
  let {
    promptJson,
    mateTraitId,
    matePrompt,
    mateNegativePrompt,
    ratio,
    style,
    mixStyle,
    prompt,
    negativePrompt,
    customStyle,
    sourceId,
    source,
    mates,
    isNsfw,
    featureType,
    isNotShown,
  } = entity;

  if (!entity) return;
  if (!mates) mates = [];
  if (mates.length > 0) initTraitFromMates(mates);
  oldDataFormat(entity);

  reqData.value = JSON.parse(
    JSON.stringify(
      decodePosesFromEnv({
        sourceId: sourceId ?? undefined,
        source: source ?? undefined,
        mates,
        ...promptJson!,
        prompt,
        negativePrompt,
        matePrompt,
        mateNegativePrompt,
        mateTraitId: mateTraitId ?? undefined,
        ratio,
        ...initStyleWithInitData(style, customStyle, mixStyle),
        amount: 1,
        isAddNSFWTag: true,
        isUnlimited: !!isNsfw,
        featureType,
        isNotShown: !!isNotShown,
      })
    )
  );
};
const writeBackByTaskId = async (taskId: number) => {
  const task = await taskNewStore.getTaskOrFetch(taskId);
  if (task) writeBackByTextImage(JSON.parse(JSON.stringify(task.entity)));
};

// 数据兼容 promptJson -> env, mates -> pose
const oldDataFormat = (entity: TextImage) => {
  const { promptJson, mates } = entity;
  if (mates && mates.length > 0) {
    // TODO...
    mates.forEach((mate: any) => {
      const { faceExpression, clothing } = mate;
      mate.pose.prompt += "," + trim(faceExpression.prompt, ",");
      mate.pose.prompt += "," + trim(clothing.prompt, ",");
      mate.pose.negativePrompt +=
        "," + trim(faceExpression.negativePrompt, ",");
      mate.pose.negativePrompt += "," + trim(clothing.negativePrompt, ",");
    });
  } else if (promptJson?.env) {
    entity.prompt = trim(promptJson.env.prompt);
    trim(promptJson.faceExpression.prompt, ",") &&
      (entity.prompt += "," + trim(promptJson.faceExpression.prompt, ","));
    trim(promptJson.clothing.prompt, ",") &&
      (entity.prompt += "," + trim(promptJson.clothing.prompt, ","));
    trim(promptJson.pose.prompt, ",") &&
      (entity.prompt += "," + trim(promptJson.pose.prompt, ","));

    entity.negativePrompt = trim(promptJson.env.negativePrompt);
    trim(promptJson.faceExpression.negativePrompt, ",") &&
      (entity.negativePrompt +=
        "," + trim(promptJson.faceExpression.negativePrompt, ","));
    trim(promptJson.clothing.negativePrompt, ",") &&
      (entity.negativePrompt +=
        "," + trim(promptJson.clothing.negativePrompt, ","));
    trim(promptJson.pose.negativePrompt, ",") &&
      (entity.negativePrompt +=
        "," + trim(promptJson.pose.negativePrompt, ","));
  }
};

const initTraitFromMates = async (mates: PromptJsonMateTrait[]) => {
  let promises: any[] = [];
  mates.forEach((mate) =>
    promises.push(traitStore.getOrFetchTraitItem(mate.id!))
  );
  await Promise.all(promises);
};

const onConfirmGenImage = async (isConfirm?: boolean) => {
  let loadingToast: any;

  if (reqData.value.mode === 6) {
    // 更换人物模式
    if (!reqData.value.mateTraitId)
      return showFailToast({ message: t("painting.replace.selectCharacter") });

    if (!reqData.value.sourceId)
      return showFailToast({ message: t("painting.replace.selectImage") });
  }
  if (reqData.value.mode === 2 && !reqData.value.mateTraitId)
    return showFailToast({ message: t("painting.single.selectCharacter") });

  if (reqData.value.mode === 3) {
    if (!reqData.value.mates || reqData.value.mates.length < 2)
      return showFailToast({ message: t("painting.muti.selectCharacter") });
    reqData.value.mateTraitId = undefined;
    const name = checkMatesHasGirlOrBoy(reqData.value.mates!);
    if (name) {
      try {
        await showConfirmDialog({
          message: t(`painting.muti.noGirlOrBoyTips`, { name }),
          confirmButtonText: t("painting.muti.continue"),
          cancelButtonText: t("Buttons.cancel"),
        });
      } catch (e) {
        return;
      }
    }
  } else reqData.value.mates = undefined;

  if (reqData.value.mode === 10) {
    if (!reqData.value.sourceId)
      return showFailToast({ message: t("painting.emoji.selectImage") });
    if (!reqData.value.prompt)
      return showFailToast({ message: t("painting.emoji.inputPrompt") });
    reqData.value.ratio = "1:1";
  }

  if (!reqData.value.isUnlimited && !isConfirm)
    return (showNormalConfirmModal.value = true);

  try {
    loadingToast = showLoadingToast({
      duration: 0,
      message: t("Message.preparingImage"),
    });
    await taskNewStore.createTask(reqData.value);
  } catch (e: any) {
    console.log(e);
  } finally {
    loadingToast && loadingToast.close();
  }
};

const showNormalConfirmModal = ref(false);

const onOpenLimited = () => {
  if (reqData.value.isUnlimited) {
    if (reqData.value.env.media?.isNsfw)
      return showConfirmDialog({
        message: t("painting.envIsNsfwCannotCloseNsfw"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      });

    return (reqData.value.isUnlimited = false);
  }
  if (!userStore.user.isAdult) return (showConfirmAge.value = true);
  return onAgeConfirm();
};

const onAgeConfirm = async () => {
  if (!StorageGet(`painting.nsfwNotice`) && showTabs.value.includes("style")) {
    try {
      await showConfirmDialog({
        message: t("painting.nsfwNotice"),
        confirmButtonText: t("Buttons.confirm"),
        cancelButtonText: t("Buttons.cancel"),
      });
      StorageSet(`painting.nsfwNotice`, 1);
    } catch (e) {
      return false;
    }
  }
  reqData.value.isUnlimited = true;
  if (!reqData.value.customStyle?.version)
    reqData.value.customStyle = { version: 2, prompt: "" };
  return true;
};

const showConfirmAge = ref(false);

const uploadNsfwImage = async (data: { media: Media; type: string }) => {
  const { media, type } = data;
  const isOpen = await onOpenLimited();
  if (isOpen) {
    if (type === "env") {
      reqData.value.env.media = {
        id: media.id,
        url: media.url,
        isNsfw: 1,
      };
      reqData.value.env.mediaId = media.id;
    } else if (type === "source") {
      reqData.value.source = {
        id: media.id,
        url: media.url,
        isNsfw: 1,
      };
      reqData.value.sourceId = media.id;
    }
  }
};

const modeMap: any = {
  1: ["prompt", "traits", "layout", "style", "setting"],
  2: ["prompt", "traits", "layout", "style", "setting"],
  3: ["prompt", "traits", "style", "setting"],
  4: ["prompt", "layout", "style", "setting"],
  5: ["origin", "style", "setting"],
  6: ["replace", "traits", "style"],
  10: ["replace", "prompt"],
};

onMounted(async () => {
  loadingPrompts.value = true;
  const { id, mode, textImageId } = route.query;
  const taskId = Number(id ?? 0);
  const textImageUUID = textImageId ?? "";
  const _mode = Number(mode ?? 1);
  showTabs.value = modeMap[_mode];

  if (history.state.forward?.includes("/crystal")) {
    const _reqData = sessionStorageGet("reqData");
    if (_reqData) {
      reqData.value = _reqData;
      sessionStorageRemove("reqData");
    }
  } else if (taskId) {
    await writeBackByTaskId(taskId);
  } else if (textImageUUID) {
    const textImage = await textImageStore.getOrFetchTextImageByUUID(
      textImageUUID as string
    );
    if (textImage)
      writeBackByTextImage(JSON.parse(JSON.stringify(textImage)), _mode);
  }
  await Promise.all([
    promptStore.getOrFetchImgPrompts(),
    trait.getOrFetchTraitList(),
  ]);
  if (trait.showCreateTraitGuide() && trait.traitList.length === 0) {
    showCreateTraitGuide.value = true;
  }
  reqData.value.mode = _mode;
  loadingPrompts.value = false;
  useGlobalModal().openServerUpgradingModal();
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr]">
    <!-- 顶部导航 -->
    <threads-nav :show-back="true" :show-center="false">
      <template #right>
        <template
          v-if="!loadingPrompts && !userStore.isCNUser && reqData.mode !== 10"
        >
          <i class="iconfont icon-heart-fill text-[#F75ECC] text-2xl"></i>

          <VanSwitch
            :model-value="reqData.isUnlimited"
            active-color="#F75ECC"
            size="14px"
            @update:model-value="onOpenLimited"
          />
        </template>
        <button
          v-if="!loadingPrompts"
          :class="[
            'btn  btn-sm rounded',
            reqData.isUnlimited ? 'btn-private' : 'btn-primary',
          ]"
          @click="onConfirmGenImage(false)"
        >
          <i class="iconify mingcute--pic-line"></i>
          {{ $t("Buttons.generate") }}
        </button>
      </template>
    </threads-nav>
    <div
      v-if="loadingPrompts"
      class="h-full flex flex-grow items-center justify-center"
    >
      <div class="loading"></div>
    </div>
    <ImagePaintWithModeTabs
      v-else
      :req-data="reqData"
      :showTabs="showTabs"
      @upload-nsfw-image="uploadNsfwImage"
    />

    <create-trait-guide v-model:show="showCreateTraitGuide" />

    <!-- 年龄限制 -->
    <NSFWModalConfirmAge
      v-model:show="showConfirmAge"
      @enter="(showConfirmAge = false), onAgeConfirm()"
    />

    <!-- 能量块消耗 -->
    <ImageModalPowerCubeConfirm
      v-model:show="showNormalConfirmModal"
      :req-data="reqData"
      @generate="onConfirmGenImage(true)"
    />
  </div>
</template>
